import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient, Query } from "@tanstack/react-query";
import { apiFetch } from "@/lib/apiClient";
import { getProfileCountries, getProfileCurrencies, removeProfileImage, updateProfile, uploadProfileImage } from "./api";
import type { Profile, ProfilePatch } from "./schema";
import { profileSchema } from "./schema";

export const currentProfileKey = (userId: string | undefined) => ["current-profile", userId] as const;
function profileReader(query: Query) {
  return query.queryKey[0] === "raw-reader" && query.queryKey.some((part) => part === "/v1/users/me/profile" || part === "/v1/users/me/settings");
}
export async function applySavedProfile(cache: QueryClient, userId: string, profile: Profile) {
  await cache.cancelQueries({ queryKey: currentProfileKey(userId) });
  await cache.cancelQueries({ predicate: profileReader });
  // A write completing after sign-out/account switching must not revive private data.
  if (cache.getQueryData<{ user: { id: string } } | null>(["session"])?.user.id !== userId) return;
  cache.setQueryData(currentProfileKey(userId), { status: 200, body: profile });
  await cache.invalidateQueries({ predicate: profileReader });
  await cache.invalidateQueries({ queryKey: ["resource-search", userId] });
  await cache.invalidateQueries({ queryKey: ["user-discovery", userId] });
  await cache.invalidateQueries({ queryKey: ["account-access", userId] });
}
export function useProfileWrite<T>(userId: string, write: (value: T) => Promise<Profile>) {
  const cache = useQueryClient();
  return useMutation({
    mutationKey: ["profile-write", userId], scope: { id: `profile-${userId}` },
    mutationFn: write, retry: false,
    onMutate: async () => { await cache.cancelQueries({ queryKey: currentProfileKey(userId) }); },
    onSuccess: (profile) => applySavedProfile(cache, userId, profile),
  });
}
export function useUpdateProfile(userId: string) { return useProfileWrite<ProfilePatch>(userId, updateProfile); }
export function useUploadProfileImage(userId: string) { return useProfileWrite<File>(userId, uploadProfileImage); }
export function useRemoveProfileImage(userId: string) { return useProfileWrite<void>(userId, removeProfileImage); }
export function profileCurrenciesOptions(revision: string | undefined) {
  return queryOptions({ queryKey: ["profile-currencies", revision], queryFn: getProfileCurrencies, enabled: Boolean(revision), staleTime: 60 * 60 * 1000, gcTime: 24 * 60 * 60 * 1000, retry: false });
}
export function profileCountriesOptions(revision: string | undefined) {
  return queryOptions({ queryKey: ["profile-countries", revision], queryFn: getProfileCountries, enabled: Boolean(revision), staleTime: Infinity, gcTime: 24 * 60 * 60 * 1000, refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false, retry: false });
}
export function useProfileCurrencies(revision: string | undefined) {
  return useQuery(profileCurrenciesOptions(revision));
}
export function useProfileCountries(revision: string | undefined) {
  return useQuery(profileCountriesOptions(revision));
}

export function useCurrentProfile(userId: string | undefined) {
  return useQuery({
    queryKey: currentProfileKey(userId),
    // Failed refreshes retain the last successful body and mounted form draft.
    queryFn: async ({ signal }) => {
      const body = await apiFetch<unknown>("/users/me/profile", { signal });
      const parsed = profileSchema.safeParse(body);
      if (!parsed.success) throw new Error("The profile response could not be read. Please retry.");
      return { status: 200, body: parsed.data };
    },
    enabled: Boolean(userId),
    retry: false,
    staleTime: 30_000,
    gcTime: 0,
  });
}
